import { useEffect } from 'react';
import type { DayRouteGroup } from '../utils/routeGroups';

// Dashed line for the stretch that's on a boat, not a road.
function ferryStroke(): google.maps.PolylineOptions {
  return {
    strokeOpacity: 0,
    icons: [{
      icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.9, scale: 3 },
      offset: '0',
      repeat: '14px',
    }],
  };
}

/**
 * Draws every selected day's route, one colour per day. Imperative so the
 * previous lines are removed deterministically when the selection changes.
 */
export function useRoutePolylines(
  mapRef: React.RefObject<google.maps.Map | null>,
  groups: DayRouteGroup[],
  hiddenStops: Set<string>,
  enabled: boolean,
) {
  useEffect(() => {
    const map = mapRef.current;
    if (!enabled || !map) return;
    const polylines: google.maps.Polyline[] = [];
    groups.forEach(group => {
      group.legs.forEach((leg, idx) => {
        if (!leg) return;
        // `legs[i]` joins `stops[i]` to `stops[i + 1]`. With the optional filter
        // on, a leg touching a hidden stop is faded instead of dropped: we never
        // asked for the route that skips it, so the line still goes there — and
        // the faded stretch is exactly what skipping it would save.
        const detour = hiddenStops.size > 0 && (
          hiddenStops.has(group.stops[idx]?.activity.id) ||
          hiddenStops.has(group.stops[idx + 1]?.activity.id)
        );
        leg.segments.forEach(seg => {
          polylines.push(new window.google.maps.Polyline({
            path: seg.decodedPath,
            strokeColor: group.color,
            strokeWeight: detour ? 3 : 4,
            map,
            // A ferry isn't a road — dash it so the crossing reads as a boat hop.
            // A flight or transfer (ADR-0031) is a straight line: dashed too, and
            // geodesic so a long one bends the way the trip really does.
            ...(seg.mode === 'FERRY' || seg.mode === 'FLIGHT' || seg.mode === 'TRANSFER'
              ? { ...ferryStroke(), geodesic: seg.mode !== 'FERRY' }
              : { strokeOpacity: detour ? 0.25 : 0.85 }),
          }));
        });
      });
    });
    return () => { polylines.forEach(p => p.setMap(null)); };
  }, [mapRef, groups, hiddenStops, enabled]);
}
